/**
 * P404 -- constellation spacing: lanes and label de-collision.
 *
 * The overlap was measured, not guessed. On the demo estate the Flow
 * layout distributes 78 services down one column across a 560px band,
 * which is **7.3px apart** — an orb is 6-8px across and a legible label
 * needs about 14px of vertical room, so orbs touch and labels are
 * guaranteed to collide. Orbit's service ring is 17.7px apart: the orbs
 * survive, the labels do not.
 *
 * So this is a CAPACITY problem, not a dimensionality one. Two pure
 * helpers, no rendering:
 *
 * - `laneLayout` splits a band into as many lanes as the count needs,
 *   and ONLY when it needs them. A band that already fits is returned
 *   unchanged, so small estates and the existing layout contracts are
 *   untouched.
 * - `selectVisibleLabels` draws the labels that fit and drops the rest,
 *   highest priority first. A label nobody can read is not information,
 *   and overlapping text makes its NEIGHBOURS unreadable too — so the
 *   cost of drawing one is paid by the labels around it.
 */

/** Vertical room an orb needs before it touches its neighbour. */
export const MIN_NODE_GAP_PX = 14;
/** Vertical room a label needs to stay legible at 11-12px type. */
export const MIN_LABEL_GAP_PX = 15;
/** Horizontal reach of a label, for collision purposes. */
export const LABEL_REACH_PX = 96;
/** Font size the node labels render at (.constellation-nlab). */
export const LABEL_FONT_PX = 12;
/**
 * Breathing room added to each side of a label box.
 *
 * The width estimate lands within ~10% of the rendered box (measured
 * against the live estate: median 0.99, range 0.91-1.11). Without a
 * margin the labels it underestimates graze their neighbour by a couple
 * of px -- which still reads as overlapping text. Two labels that merely
 * fail to intersect are not far enough apart to be legible anyway, so
 * this is clearance, not a fudge factor on the estimate.
 */
export const LABEL_CLEARANCE_PX = 4;

/**
 * Approximate rendered width of a label, in px.
 *
 * The first version of the de-collision pass compared every pair against
 * ONE assumed horizontal reach (LABEL_REACH_PX). That is right for a
 * short name and badly wrong for a long one: "AML suspicious transaction
 * reporting" is ~200px wide, so two such labels 100px apart passed the
 * test and overlapped on screen anyway. The check reported zero
 * collisions while the view plainly had them -- a detector agreeing with
 * itself.
 *
 * Estimated rather than measured because the alternative is canvas
 * measureText, which is not pure, needs the real font to be loaded, and
 * returns 0 under jsdom -- so it could not be unit-tested, which is
 * where this logic is actually verified. Glyphs are bucketed into three
 * advance classes; the estimate runs slightly WIDE on purpose, since
 * dropping a borderline label costs one name and overlapping costs two.
 */
const NARROW = new Set("ilj|!.,:;'`()[]{}/\\ ".split(""));
const WIDE = new Set("MWmw@%".split(""));

export function estimateLabelWidth(text: string, fontPx = LABEL_FONT_PX): number {
  let em = 0;
  for (const ch of text) {
    em += NARROW.has(ch) ? 0.3 : WIDE.has(ch) ? 0.88 : 0.55;
  }
  return em * fontPx;
}

export interface LanePos {
  /** Horizontal offset from the band's anchor, in px. */
  dx: number;
  y: number;
  lane: number;
}

export interface LaneResult {
  positions: Record<string, LanePos>;
  laneCount: number;
}

/**
 * Distribute ids down a band, adding lanes only when the band cannot
 * hold them at `minGap`.
 *
 * Lanes are CONTIGUOUS blocks rather than round-robin: a column that
 * continues into a second column reads like a list, whereas
 * interleaving scatters neighbours that belong together.
 */
export function laneLayout(
  ids: string[],
  opts: {
    top: number;
    bottom: number;
    minGap?: number;
    laneStep?: number;
  },
): LaneResult {
  const { top, bottom } = opts;
  const minGap = opts.minGap ?? MIN_NODE_GAP_PX;
  const laneStep = opts.laneStep ?? 58;
  const span = Math.max(0, bottom - top);
  const positions: Record<string, LanePos> = {};

  if (ids.length === 0) return { positions, laneCount: 0 };
  if (ids.length === 1) {
    positions[ids[0]!] = { dx: 0, y: (top + bottom) / 2, lane: 0 };
    return { positions, laneCount: 1 };
  }

  const capacity = Math.max(2, Math.floor(span / minGap) + 1);
  const laneCount = Math.max(1, Math.ceil(ids.length / capacity));
  const perLane = Math.ceil(ids.length / laneCount);
  // Centre the lane fan on the anchor, so a widened band grows both
  // ways rather than drifting off one side of the canvas.
  const firstDx = -((laneCount - 1) * laneStep) / 2;

  ids.forEach((id, i) => {
    const lane = Math.min(laneCount - 1, Math.floor(i / perLane));
    const indexInLane = i - lane * perLane;
    const countInLane = Math.min(perLane, ids.length - lane * perLane);
    const y =
      countInLane === 1
        ? (top + bottom) / 2
        : top + (span * indexInLane) / (countInLane - 1);
    positions[id] = { dx: firstDx + lane * laneStep, y, lane };
  });

  return { positions, laneCount };
}

export interface LabelCandidate {
  id: string;
  /** The LABEL's anchor point, not the node's centre. */
  x: number;
  y: number;
  /** Higher wins a contested slot. Selected/matched nodes go first. */
  priority?: number;
  /**
   * Rendered width in px. Omitted falls back to LABEL_REACH_PX, which
   * reproduces the original fixed-reach behaviour exactly (two centred
   * labels then clash within 96px, as before).
   */
  width?: number;
  /** Where `x` sits on the label. SVG text-anchor. */
  anchor?: "start" | "middle" | "end";
}

/**
 * Choose which labels to draw. Greedy, highest priority first, then
 * top-down so the result is stable across renders rather than
 * shuffling as unrelated state changes.
 */
/** Round 14 E3 -- a drawn orb a label must not sit under. */
export interface OrbObstacle {
  id: string;
  x: number;
  y: number;
  r: number;
}

/** Labels at or above this priority are drawn even over an orb: the
 *  selected node and a search match are the ones a reader asked for. */
const MUST_LABEL_PRIORITY = 80;

export function selectVisibleLabels(
  items: LabelCandidate[],
  opts: {
    minGapY?: number;
    minGapX?: number;
    /** Round 14 E3 -- other nodes' orbs. A name drawn under a
     *  neighbour's orb reads as that neighbour's, and a click on it
     *  lands on the orb the reader can see: 43 of 400 at a dense row. */
    orbs?: readonly OrbObstacle[];
    ascent?: number;
    descent?: number;
  } = {},
): Set<string> {
  const minGapY = opts.minGapY ?? MIN_LABEL_GAP_PX;
  const fallbackReach = opts.minGapX ?? LABEL_REACH_PX;

  // Horizontal extent of the label, which depends on where `x` sits on
  // it. A centred 200px label reaches 100px each way; a start-anchored
  // one reaches 200px to the right and nothing to the left. Treating
  // every label as a fixed reach around its anchor is what let long
  // names overlap.
  const span = (it: LabelCandidate): [number, number] => {
    const w = it.width ?? fallbackReach;
    // Only pad a measured label. A candidate with no width is using the
    // legacy fixed reach, which already bakes in its own margin --
    // padding it too would silently change behaviour for every existing
    // caller and every test written against it.
    const pad = it.width === undefined ? 0 : LABEL_CLEARANCE_PX;
    switch (it.anchor ?? "middle") {
      case "start":
        return [it.x - pad, it.x + w + pad];
      case "end":
        return [it.x - w - pad, it.x + pad];
      default:
        return [it.x - w / 2 - pad, it.x + w / 2 + pad];
    }
  };

  const ordered = [...items].sort(
    (a, b) =>
      (b.priority ?? 0) - (a.priority ?? 0) || a.y - b.y || (a.id < b.id ? -1 : 1),
  );

  const orbs = opts.orbs ?? [];
  const ascent = opts.ascent ?? 11;
  const descent = opts.descent ?? 3;
  const coversOrb = (item: LabelCandidate, l: number, r: number): boolean => {
    const top = item.y - ascent;
    const bottom = item.y + descent;
    return orbs.some((o) => {
      if (o.id === item.id) return false;
      const cx = Math.max(l, Math.min(o.x, r));
      const cy = Math.max(top, Math.min(o.y, bottom));
      const dx = o.x - cx;
      const dy = o.y - cy;
      return dx * dx + dy * dy < o.r * o.r;
    });
  };

  const placed: { span: [number, number]; y: number }[] = [];
  const visible = new Set<string>();
  for (const item of ordered) {
    const [l, r] = span(item);
    const clashes =
      placed.some(
        (p) => Math.abs(p.y - item.y) < minGapY && l < p.span[1] && r > p.span[0],
      ) ||
      ((item.priority ?? 0) < MUST_LABEL_PRIORITY && coversOrb(item, l, r));
    if (!clashes) {
      placed.push({ span: [l, r], y: item.y });
      visible.add(item.id);
    }
  }
  return visible;
}
